import React, { useEffect } from 'react';
import './Modal.css';

interface ModalProps {
  isOpen?: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Optional max width on desktop (default 560px). */
  maxWidth?: number;
  /** Hide the close (X) button. */
  hideClose?: boolean;
  /** Prevent closing when clicking the backdrop. */
  disableBackdropClose?: boolean;
  className?: string;
}

/**
 * Shared dark/gray modal.
 * - Desktop: centered card.
 * - Mobile (<=768px): full-screen borderless sheet that slides up.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen = true,
  onClose,
  title,
  children,
  footer,
  maxWidth = 560,
  hideClose = false,
  disableBackdropClose = false,
  className = '',
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="ui-modal-overlay"
      onClick={() => {
        if (!disableBackdropClose) onClose();
      }}
    >
      <div
        className={`ui-modal ${className}`}
        style={{ maxWidth: `${maxWidth}px` }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {(title || !hideClose) && (
          <div className="ui-modal-header">
            <h3 className="ui-modal-title">{title}</h3>
            {!hideClose && (
              <button
                className="ui-modal-close"
                onClick={onClose}
                aria-label="Close"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className="ui-modal-body">{children}</div>
        {footer && <div className="ui-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
